import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu } from '@arco-design/web-react';
/* 收展图标用双向箭头（« / »）：与导航列表的线条图标区分，静止态不被读成"文本缩进"。 */
import { IconDoubleLeft, IconDoubleRight, IconRobot, IconSettings } from '@arco-design/web-react/icon';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { useWorkbenchBadge } from '../store/workbenchBadge';
import { useAgentStore } from '../store/agent';
import { useRbacBootstrap } from '../hooks/useRbac';
import {
  FEATURES,
  NAV_ANCHORS,
  featureById,
  featureByPathname,
  type Feature,
} from '../registry/features';
import { sidebarNav } from '../registry/sidebarNav';
import { canSeeEntry, canSeePermission } from '../registry/access';
import { MENU_ICONS, DOMAIN_ICONS } from './menuIcons';

const { Sider } = Layout;

const stripQuery = (p: string) => p.split('?')[0];

/** 视口宽度低于该值视为「窄屏」：左侧菜单自动收起（自适应 + 底栏按钮手动收展） */
const NARROW_MAX_WIDTH = 1200;

/** 当前视口是否窄屏（<1200px）。无 window 时兜底返回 false。 */
function isNarrowViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < NARROW_MAX_WIDTH;
}

/** 域分组收展态的本地存储键（用户手动的收展跨刷新保留） */
const OPEN_KEYS_STORAGE = 'bcc.menu.openKeys';

/** 读取上次的域分组收展态；无记录返回 null（与「用户把分组全收起了」相区别） */
function loadOpenKeys(): string[] | null {
  try {
    const raw = localStorage.getItem(OPEN_KEYS_STORAGE);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return Array.isArray(v) ? v.filter((k) => typeof k === 'string') : null;
  } catch {
    return null;
  }
}

/** 权限裁剪：判据统一在 `registry/access.ts`（菜单只看权限码；未登记权限码=公共） */
function canSee(f: Feature, role: string, perms: Set<string>): boolean {
  return canSeePermission(f.permission, role, perms);
}

/** 菜单项标签：图标 + 文字（图标与文字 gap 由 .bcc-menu-label 统一）
 *  badge>0 时在**图标右上角**打数字角标（锚在图标上而非行尾，收起态才不会被 Arco 的
 *  收起规则 `.arco-icon{margin-right:100vw}` 把行尾文字连同角标一起推出 48px 窄栏外）。 */
function menuLabel(icon: ReactNode, title: string, badge = 0): ReactNode {
  return (
    <span className="bcc-menu-label">
      <span className="bcc-menu-label-icon">
        {icon}
        {badge > 0 ? (
          <span className="bcc-menu-badge" title={`${badge} 个在线会话有更新`}>
            {badge > 99 ? '99+' : badge}
          </span>
        ) : null}
      </span>
      <span>{title}</span>
    </span>
  );
}

/**
 * Registry 驱动的侧栏：数据源唯一为 Feature Registry。
 * 结构为「顶层锚点（仪表盘/坐席工作台/待办中心）+ 各业务域一级分组」。
 * 图标取自 layouts/menuIcons（域头用 DOMAIN_ICONS、条目用 MENU_ICONS 按 canonical 路由）。
 * 点击导航到 canonical Route；选中态用 Feature.id（canonical 优先，兼容旧地址）。
 * 权限码上下文由 `hooks/useRbac` 引导（守卫侧同样在调它）；后端菜单树不再用于渲染。
 *
 * 分组收展：默认只展开当前页所在域分组（20 组全展开时菜单长 2300px+，要滚数屏）。
 * localStorage `bcc.menu.openKeys` **只记用户手动的收展**；切页时自动展开当前分组只并入内存态、不落存储——
 * 否则「逛过的分组」会被记成用户偏好，用一阵子侧栏就自己长成全展开。
 * 切页还会把当前项滚入可视区，否则深链/跳转进来后看不出自己在哪。
 *
 * 底栏：唯一入口是「界面设置」抽屉（主题/线框/界面偏好/提醒），全站不得另造主题切换入口。
 * 收展：窄屏（<1200px）自动收起，宽屏下由底栏右侧按钮手动收展。
 */
export default function RegistrySidebar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const openSettings = useThemeStore((s) => s.openDrawer);
  /**
   * 进入坐席台**自动收起**侧边菜单（电话/在线都要横向空间给客户信息与对话）：
   * 进入 /workbench 时收起、离开时还原为视口默认档；坐席在台内手动展开过就不再干预
   * （manualToggleRef），否则「自动收起」会把人的明确操作反复顶回去。
   */
  const WORKBENCH_PATHS = ['/workbench'];
  const isWorkbenchPath = (p: string) => WORKBENCH_PATHS.includes(p);
  const [collapsed, setCollapsed] = useState<boolean>(() => isNarrowViewport() || isWorkbenchPath(pathname));
  const prevNarrow = useRef<boolean>(isNarrowViewport());
  const prevPathRef = useRef(pathname);
  /** 当前收起是不是本页自动收起的（决定离开工作台时要不要还原） */
  const autoCollapsedRef = useRef(isWorkbenchPath(pathname));
  /** 工作台内是否手动收展过（手动过就不再自动还原，尊重人的选择） */
  const manualToggleRef = useRef(false);
  const role = useAuthStore((s) => s.user?.role) || 'agent';
  const rbac = useAuthStore((s) => s.rbac);
  const perms = rbac?.permissions;
  // RBAC 上下文（角色/权限码）由 hooks/useRbac 统一引导：守卫侧也在调它，
  // 因为「凭权限码才能进」的页面必须先有上下文才能判定（见该文件注释）。
  useRbacBootstrap();

  // 路由进出工作台：进入即收起；离开时若当初是自动收的（且坐席没手动改过）就还原
  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = pathname;
    if (prev === pathname) return;
    if (isWorkbenchPath(pathname)) {
      manualToggleRef.current = false;
      autoCollapsedRef.current = true;
      setCollapsed(true);
      return;
    }
    if (isWorkbenchPath(prev) && autoCollapsedRef.current && !manualToggleRef.current) {
      autoCollapsedRef.current = false;
      setCollapsed(isNarrowViewport());
    }
  }, [pathname]);

  // 窄屏自适应：只在跨过阈值那一刻切换收展，用户在同一档宽内的手动收展不被覆盖
  useEffect(() => {
    const onResize = () => {
      const narrow = isNarrowViewport();
      if (narrow !== prevNarrow.current) {
        prevNarrow.current = narrow;
        setCollapsed(narrow);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const sections = useMemo(() => sidebarNav(), []);
  const permSet = useMemo(() => new Set(perms || []), [perms]);
  // 在线会话更新角标（本人承办会话的新访客消息/新派单）：只挂在「坐席工作台」锚点上
  const wbBadge = useWorkbenchBadge((s) => s.ids.length);
  // 坐席绑定态（`GET /agents/me` 由 BasicLayout 统一引导）：坐席工作台锚点的可见性判据之一——
  // 未绑定坐席档案的账号（内置 admin、未绑档案的主管）不显示该入口；未加载时按未绑定处理（见 access.ts）
  const agentBound = useAgentStore((s) => !!s.agentId);

  const selected = useMemo(() => {
    const anchor = NAV_ANCHORS.find((a) => a.route === pathname);
    if (anchor) return anchor.route;
    const exact = featureByPathname(pathname);
    if (exact?.nav) return exact.id;
    const legacy = FEATURES.find(
      (f) => f.nav && (f.legacyLocations ?? []).some((loc) => stripQuery(loc) === pathname),
    );
    return legacy?.id;
  }, [pathname]);

  // 当前页所在域分组：初始展开它、切页时自动展开（20 组全展开会让菜单长到 4000px+，被迫滚数屏找入口）
  const activeGroupKey = useMemo(() => {
    for (const s of sections) {
      for (const g of s.groups) {
        if (g.items.some((f) => f.id === selected)) return g.key;
      }
    }
    return null;
  }, [sections, selected]);

  // 侧栏默认展开档（界面设置 → 侧栏展开）：`all` 一次铺开、`pinned` 常开勾选的分组、
  // `current` 只展开当前页所在分组（默认）。此档只决定**进入/切档那一刻**的展开集合，
  // 之后的手动收展仍按 bcc.menu.openKeys 记忆——用户收起一个"常开"分组后不该被立刻弹回来。
  const expandMode = useThemeStore((s) => s.menuExpand);
  const pinned = useThemeStore((s) => s.menuPinned);
  // pinned 是数组，进依赖会每次渲染都变：用 join 做稳定依赖
  const pinnedDep = pinned.join(',');

  const [openKeys, setOpenKeys] = useState<string[]>(() => loadOpenKeys() ?? []);
  const lastScrolledTo = useRef<string | null>(null);
  const allGroupKeys = useMemo(() => sections.flatMap((s) => s.groups.map((g) => g.key)), [sections]);
  // 已应用过的档位签名：只在"档位或勾选真的变了"时重算展开集合，
  // 否则每次渲染都会把用户的手动收展冲掉
  const expandSig = useRef('');
  useEffect(() => {
    const prev = expandSig.current;
    if (expandMode === 'current') {
      expandSig.current = 'current';
      // 首屏即 current（初值已是手动记忆）与重复触发都不必重写
      if (prev === '' || prev === 'current') return;
      // 从 all/pinned 切回来：收回此前铺开的分组，只留手动记忆（当前页分组由下面的 effect 并入）
      setOpenKeys(loadOpenKeys() ?? []);
      return;
    }
    const want = expandMode === 'all' ? allGroupKeys : pinned;
    const sig = `${expandMode}|${want.join(',')}`;
    if (prev === sig) return;
    expandSig.current = sig;
    // 只并入、不收回：`all` 铺开全部分组，`pinned` 在手动记忆之上常开勾选分组
    setOpenKeys((ks) => [...new Set([...want, ...ks])]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandMode, pinnedDep, allGroupKeys]);

  // 自动展开当前分组：只并入、不收起其它分组（用户手动的收展不被切页覆盖）。
  // 注意**不写回 localStorage**：写回会把「逛过的分组」也记成用户偏好，
  // 用一段时间后侧栏就会自己长成全展开（见 handleToggleGroup 的持久化口径）。
  useEffect(() => {
    if (!activeGroupKey) return;
    setOpenKeys((ks) => (ks.includes(activeGroupKey) ? ks : [...ks, activeGroupKey]));
  }, [activeGroupKey]);

  /** 用户手动收展分组：只有这里的意图才进记忆（自动展开不留痕） */
  const handleToggleGroup = (_key: string, keys: string[]) => {
    setOpenKeys(keys);
    localStorage.setItem(OPEN_KEYS_STORAGE, JSON.stringify(keys));
  };

  // 当前项滚入侧栏可视区（菜单可长达数屏；排到下一帧，等分组展开的 DOM 落地后再量位置）
  useEffect(() => {
    if (!selected || lastScrolledTo.current === selected) return;
    const raf = requestAnimationFrame(() => {
      lastScrolledTo.current = selected;
      const scroller = document.querySelector<HTMLElement>('.bcc-sider .arco-layout-sider-children');
      const item = document.querySelector<HTMLElement>('.bcc-sider .bcc-sider-menu .arco-menu-selected');
      if (!scroller || !item) return;
      const s = scroller.getBoundingClientRect();
      const i = item.getBoundingClientRect();
      if (i.top < s.top || i.bottom > s.bottom) scroller.scrollTop += i.top - s.top - 8;
    });
    return () => cancelAnimationFrame(raf);
  }, [selected, openKeys]);

  return (
    <Sider
      theme="light"
      width={220}
      collapsedWidth={48}
      collapsible
      collapsed={collapsed}
      onCollapse={setCollapsed}
      className="bcc-sider"
      trigger={
        /* 底栏：左「界面设置」入口 + 分隔线 + 右收展按钮，挂在 Arco trigger 条内（不另造栏）。
         * 条本身保持透明：侧栏的 --bcc-sider-bg 渐变由此连续贯通到栏底，另铺一层渐变会断色成一道浅带。
         * 两个动作都必须由按钮发起——trigger 条默认整条 220×48 可点（点空白也收展），
         * 故底栏自身阻止冒泡，收展只认右侧按钮。
         * 收起态（48px 宽）两个按钮等分整条宽度、去掉内边距（每侧约 22px），高度与展开态一样是 30px。 */
        <span
          className={'bcc-sider-foot' + (collapsed ? ' bcc-sider-foot-mini' : '')}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="bcc-sider-foot-btn"
            title="界面设置"
            aria-label="界面设置"
            onClick={() => openSettings()}
          >
            <IconSettings />
            {collapsed ? null : <span className="bcc-sider-foot-label">设置</span>}
          </button>
          <span className="bcc-sider-foot-sep" aria-hidden="true" />
          <button
            type="button"
            className="bcc-sider-foot-btn"
            title={collapsed ? '展开左侧菜单' : '收起左侧菜单'}
            aria-label={collapsed ? '展开左侧菜单' : '收起左侧菜单'}
            aria-expanded={!collapsed}
            onClick={() => {
              // 手动收展：记一笔，离开工作台时不再被「自动还原」覆盖
              manualToggleRef.current = true;
              setCollapsed(!collapsed);
            }}
          >
            {collapsed ? <IconDoubleRight /> : <IconDoubleLeft />}
          </button>
        </span>
      }
    >
      <div
        className={'bcc-logo-brand' + (collapsed ? ' bcc-logo-brand-mini' : '')}
        title={collapsed ? 'Lychee 智能客服' : undefined}
      >
        {collapsed ? (
          <span className="bcc-logo-brand-mini-glyph">
            <IconRobot />
          </span>
        ) : (
          <>
            Lychee 智能客服
            <span className="bcc-logo-version">v{__APP_VERSION__}</span>
            <span className="bcc-logo-stars" aria-hidden="true">
              <i className="bcc-logo-star" />
              <i className="bcc-logo-star" />
              <i className="bcc-logo-star" />
            </span>
          </>
        )}
      </div>
      <Menu
        theme="light"
        className="bcc-sider-menu"
        selectedKeys={selected ? [selected] : []}
        openKeys={openKeys}
        onClickMenuItem={(key) => {
          const f = featureById(key);
          navigate(f ? f.route : key);
        }}
        onClickSubMenu={handleToggleGroup}
        style={{ width: '100%' }}
      >
        {NAV_ANCHORS.filter((a) => canSeeEntry(a, role, permSet, { agentBound })).map((a) => (
          <Menu.Item key={a.route}>
            {menuLabel(MENU_ICONS[a.route], a.title, a.route === '/workbench' ? wbBadge : 0)}
          </Menu.Item>
        ))}
        {sections.map((s) => {
          const groups = s.groups
            .map((g) => ({ ...g, items: g.items.filter((f) => canSee(f, role, permSet)) }))
            .filter((g) => g.items.length > 0);
          if (groups.length === 0) return null;
          const groupNodes = groups.map((g) => {
            const itemNodes = g.items.map((f) => {
              const iconKey = f.route || (f.legacyLocations ?? [])[0] || '';
              return <Menu.Item key={f.id}>{menuLabel(MENU_ICONS[iconKey], f.title)}</Menu.Item>;
            });
            // flat 分组：直接平铺为分区子项（不渲染分组头），用于单条入口（如 系统设置 / 故障诊断）
            return g.flat ? (
              <Fragment key={g.key}>{itemNodes}</Fragment>
            ) : (
              <Menu.SubMenu key={g.key} title={menuLabel(DOMAIN_ICONS[g.key], g.title || s.title)}>
                {itemNodes}
              </Menu.SubMenu>
            );
          });
          // 分区标题（业务/平台/系统）：域分组平铺一列时，业务区一个人占 12 组，与平台/系统之间无从分辨。
          // 只在可见组数 ≥3 时渲染——权限裁剪后只剩 1~2 组的分区加了反而"标签比内容多"；
          // 用 Menu.ItemGroup 而非 disabled Menu.Item（后者会往菜单里塞不可点却仍参与 ARIA/键盘序列的项）。
          if (groups.length < 3) return <Fragment key={s.section}>{groupNodes}</Fragment>;
          return (
            <Menu.ItemGroup key={`__section__${s.section}`} title={s.title}>
              {groupNodes}
            </Menu.ItemGroup>
          );
        })}
      </Menu>
    </Sider>
  );
}
